import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GraduationCap, Layers, Workflow } from "lucide-react";
import { content as contentApi, type ContentItem } from "@/api/client";
import { PERFORMANCE_DOMAINS, FOCUS_AREAS, labelFor } from "@/lib/pmbok";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "@/components/SectionHeader";
import { EmptyState } from "@/components/EmptyState";
import { MetricCard, type MetricTone } from "@/components/MetricCard";
import { cn } from "@/lib/utils";

// The Study view repivots the live content calendar through the PMBOK 8 lens
// for exam prep: each piece is already tagged with a Performance Domain
// (`pillar`) and a Focus Area (`focus_area`, our funnel-stage repurposing), so
// this is a pure client-side grouping of `content.list()` — no schema or
// worker change. Toggle the grouping dimension; tap a card to filter.

type Dim = "pillar" | "focus_area";

const DIMENSIONS: { key: Dim; label: string; groups: readonly { slug: string; label: string }[] }[] = [
  { key: "pillar", label: "Performance Domain", groups: PERFORMANCE_DOMAINS },
  { key: "focus_area", label: "Focus Area", groups: FOCUS_AREAS },
];

// Tones cycle across group cards for visual separation; "destructive" (red)
// is reserved for genuine error states, so it's left out of the rotation.
const CARD_TONES: MetricTone[] = ["primary", "secondary", "accent", "muted"];
const UNTAGGED = "__untagged__";

export function StudyPage() {
  const [dim, setDim] = useState<Dim>("pillar");
  const [active, setActive] = useState<string | null>(null);

  const contentQ = useQuery({ queryKey: ["content"], queryFn: () => contentApi.list() });
  const items = contentQ.data?.content ?? [];

  // Group items in canonical PMBOK order, with any untagged items collected
  // into a trailing bucket so nothing is silently dropped from the count.
  const grouped = useMemo(() => {
    const def = DIMENSIONS.find((d) => d.key === dim) ?? DIMENSIONS[0];
    const byKey = new Map<string, ContentItem[]>();
    for (const it of items) {
      const slug = (it[dim] ?? UNTAGGED) as string;
      const arr = byKey.get(slug);
      if (arr) arr.push(it);
      else byKey.set(slug, [it]);
    }
    for (const arr of byKey.values()) {
      arr.sort((a, b) => {
        const sa = a.scheduled_for ?? Number.MAX_SAFE_INTEGER;
        const sb = b.scheduled_for ?? Number.MAX_SAFE_INTEGER;
        return sa !== sb ? sa - sb : a.sort_order - b.sort_order;
      });
    }
    const ordered = def.groups.map((g) => ({
      slug: g.slug,
      label: g.label,
      items: byKey.get(g.slug) ?? [],
    }));
    const untagged = byKey.get(UNTAGGED);
    if (untagged && untagged.length > 0) {
      ordered.push({ slug: UNTAGGED, label: "Untagged", items: untagged });
    }
    return ordered;
  }, [items, dim]);

  const total = items.length;
  const tagged = useMemo(() => items.filter((it) => it[dim] != null).length, [items, dim]);
  const visible = active ? grouped.filter((g) => g.slug === active) : grouped;

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="text-3xl font-bold">Study.</h1>
        <p className="mt-1 text-muted-foreground">
          Your content calendar through the PMBOK 8 lens — every piece grouped by the
          domain it exercises, for exam review.
        </p>
      </header>

      <div className="inline-flex rounded-lg border bg-card p-0.5 text-sm">
        {DIMENSIONS.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => {
              setDim(d.key);
              setActive(null);
            }}
            className={cn(
              "rounded-md px-3 py-1.5 font-medium transition-colors",
              dim === d.key
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            By {d.label}
          </button>
        ))}
      </div>

      {contentQ.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : total === 0 ? (
        <EmptyState
          icon={GraduationCap}
          label="Nothing to study yet"
          hint="Add items to the planner and tag each with a PMBOK domain — they'll show up here, grouped for review."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {grouped.map((g, i) => (
              <MetricCard
                key={g.slug}
                icon={dim === "pillar" ? Layers : Workflow}
                tone={g.slug === UNTAGGED ? "muted" : CARD_TONES[i % CARD_TONES.length]}
                label={g.label}
                value={g.items.length}
                caption={`${Math.round((g.items.length / total) * 100)}% of plan`}
                onClick={() => setActive(active === g.slug ? null : g.slug)}
                active={active === g.slug}
              />
            ))}
          </div>

          {active ? (
            <button
              type="button"
              onClick={() => setActive(null)}
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              ← Show all
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">
              {tagged} of {total} items tagged
              {tagged < total ? " — untagged shown last" : ""}.
            </p>
          )}

          <div className="space-y-8">
            {visible.map((g) => (
              <section key={g.slug}>
                <SectionHeader title={g.label} count={g.items.length} />
                {g.items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing tagged here yet.</p>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      {g.items.map((it) => (
                        <StudyRow key={it.id} item={it} dim={dim} />
                      ))}
                    </CardContent>
                  </Card>
                )}
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StudyRow({ item, dim }: { item: ContentItem; dim: Dim }) {
  // Show the *other* dimension as a cross-reference tag (focus area when
  // grouped by domain, and vice versa) so each card reads as a full flashcard.
  const crossSlug = dim === "pillar" ? item.focus_area : item.pillar;
  const crossLabel =
    dim === "pillar" ? labelFor(FOCUS_AREAS, crossSlug) : labelFor(PERFORMANCE_DOMAINS, crossSlug);

  return (
    <div className="border-b px-4 py-3 last:border-b-0">
      <div className="font-medium leading-snug">{item.title}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        {item.channel_name ? <span>{item.channel_name}</span> : null}
        <span className="capitalize">{item.status}</span>
        {crossSlug ? <span className="rounded-full bg-muted px-2 py-0.5">{crossLabel}</span> : null}
      </div>
      {item.notes ? (
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{item.notes}</p>
      ) : null}
    </div>
  );
}
